'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { FolderOpen, PanelLeft, PanelRight, FileText, MessageSquarePlus, MessageSquare, Waypoints } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PisrsIcon } from '@/components/icons/PisrsIcon';
import { SettingsDialog } from '@/components/settings/SettingsDialog';
import { InfoDialog } from '@/components/InfoDialog';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { UserMenu } from '@/components/auth/UserMenu';
import { ModelBadge } from '@/components/chat/ModelBadge';
import { useModelStore } from '@/lib/stores/model-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useZadevaStore } from '@/lib/stores/zadeva-store';
import { useDocumentViewerStore } from '@/lib/stores/document-viewer-store';
import type { DossierCard } from '@/lib/stores/dossier-store';

// Stable empty array to prevent getServerSnapshot infinite loop during SSR
const EMPTY_DOSSIER: DossierCard[] = [];

export function Header() {
  const pathname = usePathname();
  const router = useRouter();
  // Gumbi za panele (Zadeve/Dokument) delujejo samo v chat postavitvi — na
  // drugih straneh (/pravni-red, /kg, /nadzor) toggle ne naredi nič vidnega,
  // zato tam kažemo gumb za vrnitev v pogovor (uporabnikova najdba, 19.7.).
  const onChatPage = pathname === '/' || pathname?.startsWith('/chat') || pathname?.startsWith('/zadeve');
  const { toggleDossier } = useLayoutStore();
  const selectedModelId = useModelStore((s) => s.modelId);
  const dossierCards = useZadevaStore((s) => {
    const z = s.zadeve.find((z) => z.id === s.activeZadevaId);
    return z?.dossierCards || EMPTY_DOSSIER;
  });

  const hasDocument = useDocumentViewerStore((s) => s.document !== null);
  const docViewerOpen = useDocumentViewerStore((s) => s.isOpen);
  const toggleDocViewer = useDocumentViewerStore((s) => s.togglePanel);

  return (
    <header className="h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-full items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <Link href="/" className="flex items-center gap-2">
            <PisrsIcon className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-cormorant), serif' }}>TRIBONIANUS</span>
          </Link>
          {/* GaMS migracija: badge slovenskega modela, ko je izbran GaMS/custom */}
          <ModelBadge modelId={selectedModelId} variant="header" />
        </div>

        {/* Navigation */}
        <nav className="flex items-center gap-2">
          {/* Nov pogovor — ustvari novo zadevo + jo aktivira */}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => {
              const id = useZadevaStore.getState().createZadeva();
              useZadevaStore.getState().setActiveZadeva(id);
              if (!onChatPage) router.push('/chat');
            }}
            title="Nov pogovor (nova zadeva)"
          >
            <MessageSquarePlus className="h-4 w-4" />
            <span className="hidden sm:inline">Nov pogovor</span>
          </Button>

          {onChatPage ? (
            <>
              {/* Zadeve Toggle (left panel) */}
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                onClick={toggleDossier}
              >
                <PanelLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Zadeve</span>
                {dossierCards.length > 0 && (
                  <Badge variant="secondary" className="ml-0.5 text-[10px] h-4 px-1">
                    {dossierCards.length}
                  </Badge>
                )}
              </Button>

              {/* Dokument Toggle (right panel) */}
              <Button
                variant={docViewerOpen ? 'secondary' : 'ghost'}
                size="sm"
                className="gap-1.5"
                onClick={toggleDocViewer}
                disabled={!hasDocument}
                title={hasDocument ? (docViewerOpen ? 'Zapri dokument' : 'Odpri dokument') : 'Ni odprtega dokumenta'}
              >
                <PanelRight className="h-4 w-4" />
                <span className="hidden sm:inline">Dokument</span>
              </Button>
            </>
          ) : (
            /* Izven chat postavitve: vrnitev v pogovor */
            <Button variant="ghost" size="sm" className="gap-1.5" asChild>
              <Link href="/chat" title="Nazaj na pogovor">
                <MessageSquare className="h-4 w-4" />
                <span className="hidden sm:inline">Pogovor</span>
              </Link>
            </Button>
          )}

          {/* Pravni red — sistemski pregled po področjih iz grafa znanja (§I.2) */}
          <Button variant={pathname?.startsWith('/pravni-red') ? 'secondary' : 'ghost'} size="sm" className="gap-1.5" asChild>
            <Link href="/pravni-red" title="Pregled pravnega reda po področjih (graf znanja)">
              <Waypoints className="h-4 w-4" />
              <span className="hidden lg:inline">Pravni red</span>
            </Link>
          </Button>

          {/* Burger z vsemi (pod)stranmi — admin sklopa Atlas/Nadzor le adminu */}
          <AppMenu />

          {/* Separator */}
          <div className="h-6 w-px bg-border mx-1" />

          {/* Info — UI-INFO: zmogljivosti in omejitve */}
          <InfoDialog />

          {/* Settings */}
          <SettingsDialog />

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* User Menu */}
          <UserMenu />
        </nav>
      </div>
    </header>
  );
}
